import { useDebounce } from "@utils/debounce";
import React, { useEffect, useState } from "react";
import { FlatList, View, StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { ORDER_FLOW_EVENTS } from "@constants/analyticsEventsCT";
import { actions } from "@reducers/cartReducer";
import { actions as customerActions } from "@reducers/customerReducer";
import analytics from "@services/analytics";
import metrics from "@constants/metrics";
import SearchBar from "@components/SearchBar";
import t from "@constants/localization";
import useCartActions from "../../components/CMS/useCartActions";
import { CustomerRow, Seprator } from "../../components/Customer/CustomerRow";
import { Header } from "../../components/Customer/Header";
import ModalLoader from "../../components/ModalLoader";
import { getAllTeamLeaderUsers } from "../../services/api";
import testIds from "@constants/testIds";
import useLeaderState from "@hooks/useLeaderState";

const PATH = "screens/Customer/Customer";

const Customer = (props) => {
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const debounceSearchTerm = useDebounce(searchText, 100);

  const { user } = useSelector((state) => ({
    user: state.auth.user,
    fetching: state.cart.fetching,
  }));

  const { canLeaderUseApp } = useLeaderState();
  const is_suspended =
    user?.is_suspended || user?.leader_state === "LEADER_SUSPENDED";

  const fromCustomer = props.route.params?.fromCustomer ?? false;
  const sku_id = props.route.params?.sku_id ?? false;

  const { addCart } = useCartActions();

  useEffect(() => {
    getAllTeamLeaderUsers()
      .then((response) => {
        setCustomers(response.data || []);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    const text = debounceSearchTerm.toUpperCase();
    if (!text) {
      setFiltered(customers);
      return;
    }
    setFiltered(
      customers.filter((item) => {
        const name =
          typeof item.user_name === "string"
            ? item.user_name.toUpperCase()
            : item.user_name ?? "";
        return name.indexOf(text) > -1;
      }),
    );
  }, [debounceSearchTerm, customers]);

  const placeOrder = (item) => {
    try {
      if (sku_id) {
        setIsLoading(true);
        addCart(sku_id, item.user_id, () => {
          analytics.trackEvent(ORDER_FLOW_EVENTS.PRODUCT_ADDED, {
            ...props.route.params,
          });
          dispatch(customerActions.setSelectedContact(item));
          props.navigation.goBack();
          setIsLoading(false);
        });
      } else {
        setIsLoading(true);
        dispatch(
          actions.viewCartLiteSuccess(
            { cartTotal: {}, result: [] },
            item.user_id,
          ),
        );
        dispatch(actions.viewCartLiteRequest(item));
        dispatch(customerActions.setSelectedContact(item));
        setIsLoading(false);
        props.navigation.goBack();
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const onRowPress = (item) => {
    analytics.trackEvent(ORDER_FLOW_EVENTS.CX_SELECTED, {
      cx_id: item.user_id,
      cx_name: item.user_name,
      source: "My_Cx",
    });
    placeOrder(item);
  };

  const renderItem = ({ item }) =>
    fromCustomer ? null : (
      <CustomerRow
        customer={fromCustomer}
        showIcon={false}
        orderClick={() => {
          onRowPress(item);
          analytics.trackEvent(ORDER_FLOW_EVENTS.PLACE_ORDER_CLICKED, {
            screen: "CxSelect",
          });
        }}
        disabled={!!is_suspended}
        data={item}
      />
    );

  const _ListHeaderComponent = () =>
    !searchText && !fromCustomer ? (
      <CustomerRow
        orderClick={() => {
          analytics.trackEvent(ORDER_FLOW_EVENTS.PLACE_ORDER_CLICKED, {
            screen: "CxSelect",
          });
        }}
        disabled={is_suspended}
        data={{
          user_phone: `+91 - ${user.phone_number}`,
          user_name: t(`${PATH}/forYourself`),
        }}
      />
    ) : null;

  const handleAddCustomer = () => {
    if (!canLeaderUseApp()) {
      return;
    }
    fromCustomer
      ? props.navigation.navigate("AddCustomer", { fromCustomer: true })
      : props.navigation.navigate("AddCustomer", { sku_id });
  };

  return (
    <View testID={testIds.Customer_Select_Kare_Screen} style={{ flex: 1 }}>
      <ModalLoader loading={isLoading} />
      <Header
        onBackClick={() => props.navigation.goBack()}
        isBtn={true}
        addContactPress={handleAddCustomer}
        title={t(`${PATH}/selectCustomer`)}
      />
      <SearchBar
        clearSearch
        customStyles={styles.searchContainer}
        query={searchText}
        placeholder={t(`${PATH}/searchCustomer`)}
        shouldShowIcon
        handleQueryChange={(value) => setSearchText(value)}
        onPressRightIcon={() => setSearchText("")}
      />
      <View style={{ flex: 1 }}>
        <FlatList
          data={filtered}
          keyExtractor={(item, index) => `${item.user_id}_${index}`}
          ItemSeparatorComponent={Seprator}
          renderItem={renderItem}
          ListHeaderComponent={_ListHeaderComponent}
        />
      </View>
    </View>
  );
};

export default Customer;

const styles = StyleSheet.create({
  searchContainer: {
    padding: metrics.baseGutter,
    backgroundColor: "#fff",
  },
});
